import {
  ALERT_MANAGER_EVENT_TYPE_MODAL,
  ALERT_MANAGER_EVENT_TYPE_MODAL_WITH_PROMISE,
  INSTANCE_DEFAULT,
} from '../../utils/spds/constants/alert-manager';
import Service, { service } from '@ember/service';

/**
 * SPDS Modal Service
 *
 * Provides a clean API for creating and managing modals through the alert-manager event bus.
 * This service communicates with the SpdsModalContainerGlobal component via alert-manager broadcasts.
 *
 * @example
 * // Create a simple modal
 * const modalId = await this.spdsModal.create({
 *   modalComponent: 'transactional',
 *   title: 'Confirm Action',
 *   bodyText: 'Are you sure?',
 *   primaryBtnText: 'Yes',
 *   secondaryBtnText: 'No'
 * });
 *
 * @example
 * // Create a modal with promise-based flow control
 * try {
 *   const modalId = await this.spdsModal.createWithPromise({
 *     modalComponent: 'destructive',
 *     title: 'Delete Item',
 *     bodyText: 'This action cannot be undone',
 *     primaryBtnText: 'Delete',
 *     secondaryBtnText: 'Cancel'
 *   });
 *   // Primary button was clicked
 *   console.log('User confirmed deletion');
 * } catch (modalId) {
 *   // Secondary button was clicked or modal was closed
 *   console.log('User cancelled');
 * }
 */
export default class SpdsModalService extends Service {
  @service alertManager;

  /**
   * Closes a modal by its ID
   * @param {string} id - The modal ID to close (if not provided, closes the last modal)
   * @returns {Promise<boolean>} - Returns true if modal was closed, false if onBeforeClose prevented closure
   */
  close = async id => {
    return await this.alertManager.clear(ALERT_MANAGER_EVENT_TYPE_MODAL, INSTANCE_DEFAULT, id);
  };

  /**
   * Creates a modal and returns its ID
   * @param {Object} data - Modal configuration based on baseModal structure
   * @param {string|Object} data.modalComponent - Modal type ('acknowledgement', 'announcement', 'destructive', 'transactional', 'standard') or custom component
   * @param {string} data.title - Modal title
   * @param {string} data.bodyText - Modal body text (html-safe for standard modals)
   * @param {boolean} [data.closeOnClickOutside=false] - Whether clicking outside closes the modal
   * @param {Function} [data.onClose] - Callback when modal closes (receives modalId)
   * @param {Function} [data.onBeforeClose] - Async callback before close (can prevent closure by returning false)
   * @param {Function} [data.primaryBtnAction] - Primary button click handler (receives modalId)
   * @param {string} [data.primaryBtnText='OK'] - Primary button text
   * @param {Function} [data.secondaryBtnAction] - Secondary button click handler (receives modalId)
   * @param {string} [data.secondaryBtnText] - Secondary button text
   * @returns {Promise<string>} - The generated modal ID
   */
  create = data => {
    return this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_MODAL, data);
  };

  /**
   * Creates a modal and returns a promise that resolves/rejects based on button actions
   * This provides a promise-based flow control for modal interactions.
   *
   * The promise:
   * - Resolves with modal ID when primary button is clicked
   * - Rejects with modal ID when secondary button is clicked or modal is closed
   *
   * @param {Object} data - Modal configuration based on baseModal structure (same as create)
   * @returns {Promise<string>} - Promise that resolves with modal ID on primary button click,
   *                               rejects with modal ID on secondary button click or close
   */
  createWithPromise = data => {
    return this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_MODAL_WITH_PROMISE, data);
  };
}
